import { useState } from 'react';
import { useNavigate } from 'react-router';
import { safeParseJson } from '../lib/storage-utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Heart, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { trackCheckInSaved } from '../lib/analytics';
import { refreshMicroActions } from '../lib/micro-actions';
import { useAuth } from '../lib/auth-context';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';

export default function QuickCheckIn() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [feeling, setFeeling] = useState([50]); // 0-100 scale
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const activeRelationshipId = localStorage.getItem('tension_active_relationship');
  const relationshipsRaw = safeParseJson<unknown>(localStorage.getItem('tension_relationships'), []);
  const relationships = Array.isArray(relationshipsRaw) ? relationshipsRaw : [];
  const activeRelationship = relationships.find((r: any) => r.id === activeRelationshipId);

  const handleSubmit = async () => {
    setLoading(true);
    let savedLocally = false;

    try {
      if (!activeRelationshipId) {
        toast.error('No active relationship found');
        navigate('/relationships');
        return;
      }

      // Convert feeling (0-100) to RTI-like score
      // Lower feeling = higher tension
      const rtiScore = Math.round(100 - feeling[0]);

      // Calculate simplified components based on feeling
      const components = {
        initiation: feeling[0],
        latency: feeling[0],
        emotionalDensity: 100 - feeling[0],
        availability: feeling[0],
        withdrawal: 100 - feeling[0],
      };

      // Store check-in data in local storage
      const now = new Date().toISOString();
      const checkinData = {
        relationshipId: activeRelationshipId,
        relationshipName: activeRelationship?.name || 'Unknown',
        date: now,
        timestamp: now,
        components,
        emotionalStates: [feeling[0]],
        emotionalAverage: feeling[0],
        triggers: [],
        note: note.trim(),
        rtiScore: rtiScore,
        quickCheckIn: true, // Flag to indicate this was a quick check-in
      };

      const checkinsRaw = safeParseJson<unknown[]>(localStorage.getItem('tension_checkins'), []);
      const checkins = Array.isArray(checkinsRaw) ? [...checkinsRaw] : [];
      checkins.push(checkinData);
      
      try {
        localStorage.setItem('tension_checkins', JSON.stringify(checkins));
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
        }
        savedLocally = true;
      } catch (storageError: any) {
        if (storageError.name === 'QuotaExceededError') {
          toast.error('Storage limit reached! Please export your data or delete old check-ins.');
          throw new Error('Storage quota exceeded. Please export your data.');
        }
        throw storageError;
      }
      
      console.log('✅ Quick check-in saved:', checkinData);

      trackCheckInSaved({
        source: 'quick',
        totalCheckInsAfterSave: checkins.length,
        rtiScore,
      });

      try {
        await refreshMicroActions(rtiScore, components, { accessToken, source: 'quick' });
      } catch {
        /* non-blocking */
      }

      toast.success(`Quick check-in complete for ${activeRelationship?.name || 'relationship'}!`);
      navigate('/home');
    } catch (error: any) {
      console.error('Quick check-in error:', error);
      const rawMessage = typeof error?.message === 'string' ? error.message.trim() : '';
      const looksLikeNotFound = /\b404\b/i.test(rawMessage) || /not found/i.test(rawMessage);
      if (savedLocally && looksLikeNotFound) {
        toast.success(`Quick check-in complete for ${activeRelationship?.name || 'relationship'}!`);
        navigate('/home');
        return;
      }
      toast.error(rawMessage || 'Failed to submit check-in');
    } finally {
      setLoading(false);
    }
  };

  const getFeelingLabel = (value: number) => {
    if (value >= 80) return '🌟 Great';
    if (value >= 60) return '😊 Good';
    if (value >= 40) return '😐 Okay';
    if (value >= 20) return '😔 Struggling';
    return '😰 Very Difficult';
  };

  const getFeelingColor = (value: number) => {
    if (value >= 80) return 'text-green-600';
    if (value >= 60) return 'text-blue-600';
    if (value >= 40) return 'text-yellow-600';
    if (value >= 20) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen tension-page-bg">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          {/* Quick vs Full Toggle */}
          <button
            onClick={() => navigate('/check-in')}
            className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400 hover:underline"
          >
            <Clock className="w-4 h-4" />
            Switch to Full Check-In (2 min)
          </button>
        </div>

        {/* Relationship indicator */}
        {activeRelationship && (
          <div className="mb-6 flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Heart className="w-4 h-4 text-pink-500" aria-hidden="true" />
            <span>{activeRelationship.name}</span>
          </div>
        )}

        <Card className="space-y-8 border-violet-200/70 bg-white/92 p-8 dark:border-violet-900/45 dark:bg-neutral-900/80">
          {/* Title */}
          <div className="space-y-2 text-center">
            <div className="w-16 h-16 mx-auto bg-violet-100 dark:bg-violet-900 rounded-full flex items-center justify-center">
              <Zap className="w-8 h-8 text-violet-600 dark:text-violet-400" />
            </div>
            <h2 className="text-2xl font-light text-neutral-900 dark:text-neutral-100">Quick Check-In</h2>
            <p className="text-neutral-600 dark:text-neutral-400">Takes just 30 seconds</p>
            <div className="tension-glow-sm inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-full text-xs text-green-700 dark:text-green-300">
              <Clock className="w-3 h-3" />
              <span>~30 sec</span>
            </div>
          </div>

          {/* Main Question */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100 block">
                How are you feeling about your relationship today?
              </label>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Move the slider to rate your current feeling
              </p>
            </div>

            {/* Feeling Slider */}
            <div className="space-y-6 py-4">
              <div className="text-center">
                <div className={`text-4xl font-light ${getFeelingColor(feeling[0])}`}>
                  {getFeelingLabel(feeling[0])}
                </div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                  {feeling[0]}/100
                </div>
              </div>

              <Slider
                value={feeling}
                onValueChange={setFeeling}
                min={0}
                max={100}
                step={1}
                className="w-full"
              />

              <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
                <span>😰 Very Difficult</span>
                <span>🌟 Great</span>
              </div>
            </div>
          </div>

          {/* Optional Note */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-900 dark:text-neutral-100 block">
              Quick note (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything specific you want to remember about today?"
              maxLength={200}
              rows={3}
              className="resize-none"
            />
            <div className="text-xs text-neutral-500 dark:text-neutral-400 text-right">
              {note.length}/200
            </div>
          </div>

          {/* Info Box */}
          <div className="tension-glow bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
            <p className="text-xs text-violet-900 dark:text-violet-200">
              <strong>Quick Check-Ins</strong> provide a fast way to track your relationship mood. 
              For deeper insights, try the Full Check-In which analyzes 5 components and emotional states.
            </p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Complete Quick Check-In
              </>
            )}
          </Button>

          {/* Alternative Action */}
          <div className="text-center">
            <button
              onClick={() => navigate('/check-in')}
              className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:underline"
            >
              Want deeper insights? Try the Full Check-In instead →
            </button>
          </div>
        </Card>

        {/* Comparison */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <Card className="border-2 border-violet-200 bg-white/90 p-4 dark:border-violet-800 dark:bg-neutral-900/75">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Quick Check-In</h3>
            </div>
            <ul className="space-y-1 text-xs text-neutral-600 dark:text-neutral-400">
              <li>• 30 seconds</li>
              <li>• 1 question</li>
              <li>• Quick mood tracking</li>
              <li>• Great for busy days</li>
            </ul>
          </Card>

          <Card className="border-violet-200/70 bg-white/90 p-4 dark:border-violet-900/45 dark:bg-neutral-900/75">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Full Check-In</h3>
            </div>
            <ul className="space-y-1 text-xs text-neutral-600 dark:text-neutral-400">
              <li>• 2-3 minutes</li>
              <li>• 5 components</li>
              <li>• Detailed tension scoring</li>
              <li>• Personalized actions</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
